
import { WorldCountry } from '@/data/countries/types';

export interface DuplicateAnalysis {
  duplicateCountries: Array<{
    slug: string;
    name: string;
    count: number;
    indices: number[];
  }>;
  duplicateCities: Array<{
    countrySlug: string;
    citySlug: string;
    cityName: string;
    count: number;
  }>;
  invalidSlugs: Array<{
    type: 'country' | 'city';
    slug: string;
    name: string;
    issues: string[];
  }>;
  emptyCountries: Array<{
    slug: string;
    name: string;
  }>;
  summary: {
    totalCountries: number;
    totalCities: number;
    duplicateCountriesFound: number;
    duplicateCitiesFound: number;
    invalidSlugsFound: number;
    emptyCountriesFound: number;
  };
}

export const analyzeDataIntegrity = (countries: WorldCountry[]): DuplicateAnalysis => {
  const duplicateCountries: DuplicateAnalysis['duplicateCountries'] = [];
  const duplicateCities: DuplicateAnalysis['duplicateCities'] = [];
  const invalidSlugs: DuplicateAnalysis['invalidSlugs'] = [];
  const emptyCountries: DuplicateAnalysis['emptyCountries'] = [];

  // Análisis de países duplicados
  const countrySlugMap = new Map<string, number[]>();
  countries.forEach((country, index) => {
    if (!countrySlugMap.has(country.slug)) {
      countrySlugMap.set(country.slug, []);
    }
    countrySlugMap.get(country.slug)!.push(index);
  });

  countrySlugMap.forEach((indices, slug) => {
    if (indices.length > 1) {
      const country = countries[indices[0]];
      duplicateCountries.push({
        slug,
        name: country.name,
        count: indices.length,
        indices
      });
    }
  });

  // Análisis de ciudades duplicadas y países vacíos
  countries.forEach((country) => {
    // Verificar países vacíos
    if (country.topCities.length === 0) {
      emptyCountries.push({
        slug: country.slug,
        name: country.name
      });
    }

    // Verificar slugs de países inválidos
    const countrySlugIssues = validateSlug(country.slug);
    if (countrySlugIssues.length > 0) {
      invalidSlugs.push({
        type: 'country',
        slug: country.slug,
        name: country.name,
        issues: countrySlugIssues
      });
    }

    // Análisis de ciudades duplicadas dentro del país
    const citySlugMap = new Map<string, number>();
    country.topCities.forEach((city) => {
      const count = citySlugMap.get(city.slug) || 0;
      citySlugMap.set(city.slug, count + 1);

      // Verificar slugs de ciudades inválidos
      const citySlugIssues = validateSlug(city.slug);
      if (citySlugIssues.length > 0) {
        invalidSlugs.push({
          type: 'city',
          slug: city.slug,
          name: city.name,
          issues: citySlugIssues
        });
      }
    });

    citySlugMap.forEach((count, citySlug) => {
      if (count > 1) {
        const city = country.topCities.find(c => c.slug === citySlug);
        if (city) {
          duplicateCities.push({
            countrySlug: country.slug,
            citySlug,
            cityName: city.name,
            count
          });
        }
      }
    });
  });

  const totalCities = countries.reduce((sum, country) => sum + country.topCities.length, 0);

  return {
    duplicateCountries,
    duplicateCities,
    invalidSlugs,
    emptyCountries,
    summary: {
      totalCountries: countries.length,
      totalCities,
      duplicateCountriesFound: duplicateCountries.length,
      duplicateCitiesFound: duplicateCities.length,
      invalidSlugsFound: invalidSlugs.length,
      emptyCountriesFound: emptyCountries.length
    }
  };
};

const validateSlug = (slug: string): string[] => {
  const issues: string[] = [];
  
  if (!slug || slug.trim() === '') {
    issues.push('Slug vacío');
  }
  
  if (slug !== slug.toLowerCase()) {
    issues.push('Slug contiene mayúsculas');
  }
  
  if (!/^[a-z0-9-]+$/.test(slug)) {
    issues.push('Slug contiene caracteres inválidos');
  }
  
  if (slug.startsWith('-') || slug.endsWith('-')) {
    issues.push('Slug empieza o termina con guión');
  }
  
  if (slug.includes('--')) {
    issues.push('Slug contiene guiones dobles');
  }
  
  return issues;
};

export const cleanDuplicates = (countries: WorldCountry[]): WorldCountry[] => {
  const cleanedCountries: WorldCountry[] = [];
  const seenCountrySlugs = new Set<string>();

  countries.forEach((country) => {
    // Eliminar países duplicados (mantener el primero)
    if (seenCountrySlugs.has(country.slug)) {
      console.warn(`⚠️ País duplicado eliminado: ${country.name} (${country.slug})`);
      return;
    }
    seenCountrySlugs.add(country.slug);

    // Limpiar ciudades duplicadas dentro del país
    const seenCitySlugs = new Set<string>();
    const cleanedCities = country.topCities.filter((city) => {
      if (seenCitySlugs.has(city.slug)) {
        console.warn(`⚠️ Ciudad duplicada eliminada: ${city.name} (${city.slug}) en ${country.name}`);
        return false;
      }
      seenCitySlugs.add(city.slug);
      return true;
    });

    cleanedCountries.push({
      ...country,
      topCities: cleanedCities
    });
  });

  return cleanedCountries;
};

export const generateDataReport = (analysis: DuplicateAnalysis): string => {
  const report = [];
  
  report.push('📊 REPORTE DE ANÁLISIS DE INTEGRIDAD DE DATOS');
  report.push('='.repeat(60));
  report.push(`🌍 Total de países: ${analysis.summary.totalCountries}`);
  report.push(`🏙️ Total de ciudades: ${analysis.summary.totalCities.toLocaleString()}`);
  report.push('');
  
  if (analysis.summary.duplicateCountriesFound > 0) {
    report.push(`❌ PAÍSES DUPLICADOS ENCONTRADOS: ${analysis.summary.duplicateCountriesFound}`);
    analysis.duplicateCountries.forEach((dup, index) => {
      report.push(`   ${index + 1}. ${dup.name} (${dup.slug}) - ${dup.count} duplicados`);
    });
    report.push('');
  }
  
  if (analysis.summary.duplicateCitiesFound > 0) {
    report.push(`❌ CIUDADES DUPLICADAS ENCONTRADAS: ${analysis.summary.duplicateCitiesFound}`);
    analysis.duplicateCities.slice(0, 10).forEach((dup, index) => {
      report.push(`   ${index + 1}. ${dup.cityName} (${dup.citySlug}) en ${dup.countrySlug} - ${dup.count} duplicados`);
    });
    if (analysis.duplicateCities.length > 10) {
      report.push(`   ... y ${analysis.duplicateCities.length - 10} más`);
    }
    report.push('');
  }
  
  if (analysis.summary.emptyCountriesFound > 0) {
    report.push(`⚠️ PAÍSES SIN CIUDADES: ${analysis.summary.emptyCountriesFound}`);
    analysis.emptyCountries.forEach((country, index) => {
      report.push(`   ${index + 1}. ${country.name} (${country.slug})`);
    });
    report.push('');
  }
  
  if (analysis.summary.invalidSlugsFound > 0) {
    report.push(`⚠️ SLUGS INVÁLIDOS ENCONTRADOS: ${analysis.summary.invalidSlugsFound}`);
    analysis.invalidSlugs.slice(0, 10).forEach((invalid, index) => {
      report.push(`   ${index + 1}. ${invalid.type}: ${invalid.name} (${invalid.slug})`);
      report.push(`      Problemas: ${invalid.issues.join(', ')}`);
    });
    if (analysis.invalidSlugs.length > 10) {
      report.push(`   ... y ${analysis.invalidSlugs.length - 10} más`);
    }
    report.push('');
  }
  
  if (analysis.summary.duplicateCountriesFound === 0 && 
      analysis.summary.duplicateCitiesFound === 0 && 
      analysis.summary.emptyCountriesFound === 0 && 
      analysis.summary.invalidSlugsFound === 0) {
    report.push('✅ NO SE ENCONTRARON PROBLEMAS DE INTEGRIDAD');
    report.push('🎉 Los datos están limpios y bien estructurados');
  }
  
  return report.join('\n');
};
